import React from 'react'
import PropTypes from 'prop-types'
import {
    Container,
    Box,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import LinksList from '../../links-list'
import {renderComponentsSwitch} from '../../../components/optimizely/utils/contentRenderer'
import {transformMegaMenuBlockToCategories} from '../../../components/optimizely/utils/utils'
import {
    ITEMS_PER_COLUMN,
    MAX_CONTENT_GROUPS,
    TOTAL_GRID_COLUMNS,
    COLS_PER_GROUP,
    getImageColSpan,
    getRemainingColSpan,
    getCategoryUrl
} from './list-menu-helpers'

/**
 * Renders CMS-driven mega menu content with a 12-column grid layout.
 *
 * - Each content group spans 2 grid columns (max 6 groups).
 * - Groups with more than 15 items overflow into an adjacent column (max 2 columns per group).
 * - Images are placed inline in the grid with colSpan based on IllustrationWidth (2/3/6).
 * - Total grid columns used by groups + images must not exceed 12.
 */
const CMSMegaMenuContent = ({ item, onClose, baseStyle, categories }) => {
    const cmsColumns = transformMegaMenuBlockToCategories(item.CMSMegaMenuData)
    const illustrationBlocks = cmsColumns.illustrationBlocksFromParent || []

    // Reserve grid columns for images first, then allocate remaining to content groups
    const imageColsUsed = illustrationBlocks.reduce((sum, block) => sum + getImageColSpan(block), 0)
    const availableForGroups = TOTAL_GRID_COLUMNS - imageColsUsed
    const maxGroups = Math.min(MAX_CONTENT_GROUPS, Math.floor(availableForGroups / COLS_PER_GROUP))

    // Build grid items with 15-item overflow handling
    const gridItems = []
    let usedSlots = 0

    for (const [colIdx, column] of cmsColumns.entries()) {
        if (usedSlots >= maxGroups) break

        const items = column.categories || []
        const mainItems = items.slice(0, ITEMS_PER_COLUMN)
        const overflowItems = items.slice(ITEMS_PER_COLUMN, ITEMS_PER_COLUMN * 2)
        const hasOverflow = overflowItems.length > 0

        const columnCategoryFromAPI = categories?.[column.id]
        const columnName = column.name || columnCategoryFromAPI?.name

        const buildColumnLinks = (linkItems) => linkItems.map((categoryItem) => {
            const cmsData = categoryItem.cmsCategoryData
            const apiCategory = categories?.[categoryItem.id]
            return {
                href: getCategoryUrl(apiCategory || { id: categoryItem.id }, categories),
                text: cmsData?.OverrideName || apiCategory?.name || categoryItem.name,
                cmsCategoryReplacement: cmsData,
                c_showSeparator:
                    apiCategory?.c_showSeparator ??
                    categoryItem?.c_showSeparator,
                c_showSeparatorMobile:
                    apiCategory?.c_showSeparatorMobile ??
                    categoryItem?.c_showSeparatorMobile
            }
        })

        gridItems.push({
            key: `cms-col-${column.id || colIdx}-main`,
            header: columnName,
            links: buildColumnLinks(mainItems)
        })
        usedSlots++

        // Overflow column: continuation of the same group, no header
        if (hasOverflow && usedSlots < maxGroups) {
            gridItems.push({
                key: `cms-col-${column.id || colIdx}-overflow`,
                header: null,
                isOverflow: true,
                links: buildColumnLinks(overflowItems)
            })
            usedSlots++
        }
    }

    return (
        <Container {...baseStyle.popoverContainer}>
            <Box {...baseStyle.megaMenuGrid}>
                <Box
                    gridColumn={`span ${getRemainingColSpan(imageColsUsed)}`}
                    display="grid"
                    gridTemplateColumns={`repeat(${getRemainingColSpan(imageColsUsed)}, 1fr)`}
                    gap={baseStyle.megaMenuGrid?.gap}
                >
                    {gridItems.map(({key, header, isOverflow, links}) => (
                        <Box key={key} {...baseStyle.megaMenuGridColumn}>
                            {isOverflow ? (
                                <Text {...baseStyle.level1Label} {...baseStyle.spacerLabel} aria-hidden="true">
                                    &nbsp;
                                </Text>
                            ) : (
                                header && <Text {...baseStyle.level1Label}>{header}</Text>
                            )}
                            <LinksList
                                variant="menuItem"
                                links={links}
                                onLinkClick={onClose}
                            />
                        </Box>
                    ))}
                </Box>

                {/* Images inline in the grid with colSpan from IllustrationWidth */}
                {illustrationBlocks.map((illustrationBlock, index) => {
                    const expandedBlock = illustrationBlock?.ContentLink?.Expanded || illustrationBlock?.Expanded
                    return (
                        <Box
                            key={`cms-img-${index}`}
                            gridColumn={`span ${getImageColSpan(illustrationBlock)}`}
                        >
                            {renderComponentsSwitch(expandedBlock, {index, onClose})}
                        </Box>
                    )
                })}
            </Box>
        </Container>
    )
}

CMSMegaMenuContent.propTypes = {
    item: PropTypes.object,
    onClose: PropTypes.func,
    baseStyle: PropTypes.object,
    categories: PropTypes.object
}

export { CMSMegaMenuContent }
